import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { FileUploader } from '../notes/FileUploader';
import { AircraftNotesModelFactory } from '../fleet/AircraftNotesModelFactory';
import { AircraftLogEntryForm } from './AircraftLogEntryForm';
import { Page } from '../components/page/Page';
import { WrapWithCardOnLargerScreen } from '../components/card/WrapWithCardOnLargerScreen';
import { AlertPresenter } from '../app/AlertBinder';
import { HistoryViewModel } from '../app/HistoryViewModel';
import { useEffect } from 'react';
import { PaywallDialogViewModel } from '../paywall/PaywallDialogViewModel';
import { TrackingService } from '../support/tracking/TrackingService';
import { IAttachment } from '../notes/NotesClient';
import { IAircraftNote } from './IAircraftNote';
import { initCap } from '../support/helpers/format';
import { useSubscriptionSync } from '../support/hooks/useSubscriptionSync';

export const AircraftLogEntryAddPage: React.FC<{aircraftId: string}> = ({ aircraftId }) => {
    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);
    const tracker = useInstance(TrackingService);

    const searchParams = history.getCurrentSearchParamMap();
    const backTo = searchParams.backTo;

    const uploader = useInstance(FileUploader);
    useSubscription(() => uploader);

    // NOTE: model creation must be inside subscription, otherwise model is recreated on every emit
    const notesFactory = useInstance(AircraftNotesModelFactory);
    const noteVM = useSubscriptionSync(() => notesFactory.forAircraft(aircraftId).createNewNoteVM());

    const paywall = useInstance(PaywallDialogViewModel);
    useSubscription(() => paywall);

    useEffect(() => {
        paywall.requirePurchase({ triggerSource: 'log entry' }).then(upgraded => {
            if (!upgraded) {
                history.back('/');
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goBack = () => {
        if (backTo) {
            history.replace(backTo);
        } else {
            history.back(`/fleet/${aircraftId}/log`);
        }
    };

    const saveAndClose = async () => {
        if (!noteVM){
            return;
        }
        try {
            await noteVM.save();

            tracker.track('Drone Log Entry Added', () => {
                const note = noteVM.getNote() as IAircraftNote;
                return {
                    title: note.title,
                    dateTime: note.otherAttributes.dateTime,
                    type: initCap(note.otherAttributes.type),
                    html: note.html,
                    attachments: note.attachments
                };
            });

            goBack();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    const onAttachmentAdded = (attachment: IAttachment) => {
        tracker.track('Log Entry Attachment Uploaded', () => ({
            type: attachment.url.substring(attachment.url.lastIndexOf('.')) || ''
        }));
    };

    const onAttachmentRemoved = (attachment: IAttachment) => {
        tracker.track('Log Entry Attachment Removed', () => ({
            type: attachment.url.substring(attachment.url.lastIndexOf('.')) || ''
        }));
    };

    return (

        <Page title="New Entry" onBack={goBack} backLabel="Cancel" onAction={saveAndClose} actionLabel="Save">
            <WrapWithCardOnLargerScreen>
                {noteVM &&
                    <AircraftLogEntryForm
                        note={noteVM.getNote() as IAircraftNote}
                        noteChanged={updatedNote => noteVM.setNote(updatedNote)}
                        attachmentAdded={onAttachmentAdded}
                        attachmentRemoved={onAttachmentRemoved}
                    />
                }
            </WrapWithCardOnLargerScreen>
        </Page>
    );
};
